/**
 * کاتالوگ محصول / مدل / رنگ / سایز — گزینه‌های کشویی صفحه‌های تولید.
 * «مدل» زیرِ یک محصولِ مشخص تعریف می‌شود (رکابی/تاپ زیرِ محصولی که نرخش مشترک است)
 * و در محصولاتِ دیگر دیده نمی‌شود.
 * غیرفعال‌کردن یک گزینه فقط جلوی انتخابِ بعدی را می‌گیرد و رکوردهای قبلی دست‌نخورده می‌مانند.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, Col, Input, InputNumber, Popconfirm, Row, Segmented, Select, Space, Switch, Table, Tag, Typography, message } from "antd";
import Alert from "../../components/GxAlert";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import {
  createProductionOption, deactivateProductionOption, getProductionOptions,
  updateProductionOption,
} from "../../api/client";

const { Text } = Typography;
const KINDS = [
  { value: "product", label: "محصول" },
  { value: "variant", label: "مدل" },
  { value: "color", label: "رنگ" },
  { value: "size", label: "سایز" },
];

export default function ProductionOptionsTab() {
  const [kind, setKind] = useState("product");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [products, setProducts] = useState([]);   // برای انتخابِ صاحبِ «مدل»
  const [parent, setParent] = useState(null);

  useEffect(() => {
    getProductionOptions({ kind: "product", active_only: true })
      .then((r) => setProducts((r.data || []).map((o) => ({ value: o.value, label: o.value }))))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getProductionOptions({ kind, active_only: false });
      setRows(r.data || []);
    } catch {
      message.error("خطا در بارگذاری کاتالوگ");
    }
    setLoading(false);
  }, [kind]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const v = draft.trim();
    if (!v) return message.warning("مقدار را وارد کنید");
    try {
      if (kind === "variant" && !parent) {
        return message.warning("اول محصولِ صاحبِ این مدل را انتخاب کنید");
      }
      await createProductionOption({ kind, value: v, parent: parent || undefined });
      setDraft("");
      message.success("افزوده شد");
      load();
    } catch (e) {
      message.error(e?.response?.data?.detail || "افزودن ناموفق بود");
    }
  };

  const patch = async (id, data) => {
    try {
      await updateProductionOption(id, data);
      load();
    } catch (e) {
      message.error(e?.response?.data?.detail || "ذخیره ناموفق بود");
    }
  };

  const cols = [
    ...(kind === "variant" ? [{
      title: "محصول", dataIndex: "parent", width: 220,
      render: (v) => v || <Text type="secondary">—</Text>,
    }] : []),
    { title: "مقدار", dataIndex: "value", width: 220,
      render: (v, r) => (r.is_active ? <b>{v}</b> : <Text delete type="secondary">{v}</Text>) },
    { title: "ترتیب نمایش", dataIndex: "sort_order", width: 130,
      render: (v, r) => (
        <InputNumber size="small" min={0} defaultValue={v} style={{ width: 90 }}
          onBlur={(e) => {
            const n = Number(e.target.value);
            if (Number.isFinite(n) && n !== v) patch(r.id, { sort_order: n });
          }} />
      ) },
    { title: "فعال", dataIndex: "is_active", width: 110,
      render: (v, r) => (
        <Switch size="small" checked={v} checkedChildren="فعال" unCheckedChildren="غیرفعال"
          onChange={(x) => patch(r.id, { is_active: x })} />
      ) },
    { title: "", width: 110,
      render: (_, r) => (r.is_active ? (
        <Popconfirm title="غیرفعال شود؟ رکوردهای قبلی تغییر نمی‌کنند" okText="بله" cancelText="خیر"
          onConfirm={async () => { await deactivateProductionOption(r.id); load(); }}>
          <Button size="small" danger>غیرفعال‌سازی</Button>
        </Popconfirm>
      ) : <Tag>غیرفعال</Tag>) },
  ];

  return (
    <Card size="small" title="کاتالوگ محصول / مدل / رنگ / سایز"
      extra={<Button icon={<ReloadOutlined />} onClick={load} loading={loading}>تازه‌سازی</Button>}>
      <Row gutter={[10, 10]} style={{ marginBottom: 12 }} align="middle">
        <Col xs={24} md={10}>
          <Segmented value={kind} onChange={setKind} options={KINDS} />
        </Col>
        <Col xs={24} md={14}>
          <Space wrap>
            {kind === "variant" && (
              <Select showSearch allowClear value={parent} onChange={setParent}
                options={products} placeholder="محصولِ صاحبِ مدل"
                optionFilterProp="label" style={{ width: 220 }} />
            )}
            <Input value={draft} onChange={(e) => setDraft(e.target.value)} onPressEnter={add}
              placeholder={`${KINDS.find((k) => k.value === kind)?.label} جدید`} style={{ width: 220 }} />
            <Button type="primary" icon={<PlusOutlined />} onClick={add}>افزودن</Button>
          </Space>
        </Col>
      </Row>
      {kind === "variant" && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message="«مدل» فقط وقتی لازم است که یک ردیفِ نرخ‌نامه چند محصولِ هم‌نرخ را پوشش دهد"
          description="مثلاً «زیرپوش رکابی، رکابی و تاپ پنبه» یک نرخ دارد ولی دو محصول است؛ با مدل مشخص می‌کنید کدام است. مدل‌های هر محصول فقط زیرِ همان محصول دیده می‌شوند." />
      )}
      <Table size="small" rowKey="id" loading={loading} dataSource={rows} columns={cols}
        pagination={{ defaultPageSize: 15, showSizeChanger: true }} scroll={{ x: "max-content" }} />
      <Text type="secondary" style={{ fontSize: 12 }}>
        ترتیب نمایش کوچک‌تر بالاتر می‌آید. گزینهٔ غیرفعال از کشوی صفحه‌های تولید حذف می‌شود
        ولی روی بیجک‌ها و تولیدهای قبلی اثری ندارد.
      </Text>
    </Card>
  );
}
